import React, { useEffect, useState } from 'react';
import { FiX, FiTrash2 } from 'react-icons/fi';
import { createMeeting, updateMeeting, deleteMeeting } from '../services/api';

// Convert a Date object (local) into the value an <input type="datetime-local"> expects:
// "YYYY-MM-DDTHH:MM" with no timezone suffix.
function dateToLocalInput(d) {
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Parse a "datetime-local" string in the user's local timezone, return UTC ISO.
function localInputToUtcIso(s) {
  if (!s) return null;
  // The Date constructor interprets a no-tz string as local time.
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

// Convert an ISO string (UTC) to the local datetime-local input format.
function utcIsoToLocalInput(iso) {
  if (!iso) return '';
  // Make sure trailing Z so it's parsed as UTC, then converted to local.
  let s = iso;
  if (!s.endsWith('Z') && !s.includes('+')) s = s + 'Z';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  return dateToLocalInput(d);
}

const REMINDER_OPTIONS = [
  { value: 0, label: 'At start time' },
  { value: 5, label: '5 minutes before' },
  { value: 10, label: '10 minutes before' },
  { value: 15, label: '15 minutes before' },
  { value: 30, label: '30 minutes before' },
  { value: 60, label: '1 hour before' },
  { value: 120, label: '2 hours before' },
  { value: 24 * 60, label: '1 day before' },
];

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  fontSize: '14px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  outline: 'none',
  boxSizing: 'border-box',
};

export default function MeetingModal({ meeting, defaultDate, onClose, onSaved }) {
  const isEdit = !!meeting?.id;
  const [title, setTitle] = useState(meeting?.title || '');
  const [description, setDescription] = useState(meeting?.description || '');
  const [location, setLocation] = useState(meeting?.location || '');
  const [startInput, setStartInput] = useState(() => {
    if (meeting?.start_time) return utcIsoToLocalInput(meeting.start_time);
    if (defaultDate) {
      // Default to 9:00 AM on the given day
      const d = new Date(defaultDate);
      d.setHours(9, 0, 0, 0);
      return dateToLocalInput(d);
    }
    // Default: next half-hour
    const now = new Date();
    now.setMinutes(now.getMinutes() < 30 ? 30 : 60, 0, 0);
    return dateToLocalInput(now);
  });
  const [endInput, setEndInput] = useState(meeting?.end_time ? utcIsoToLocalInput(meeting.end_time) : '');
  const [reminder, setReminder] = useState(meeting?.reminder_minutes_before ?? 15);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { setError('Title is required'); return; }
    const startIso = localInputToUtcIso(startInput);
    if (!startIso) { setError('Start time is required'); return; }
    const endIso = endInput ? localInputToUtcIso(endInput) : null;
    if (endIso && new Date(endIso) < new Date(startIso)) {
      setError('End time must be after the start');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        title: title.trim(),
        description: description || null,
        location: location || null,
        start_time: startIso,
        end_time: endIso,
        reminder_minutes_before: reminder,
      };
      if (isEdit) {
        await updateMeeting(meeting.id, payload);
      } else {
        await createMeeting(payload);
      }
      onSaved?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save meeting');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!meeting?.id) return;
    if (!window.confirm('Delete this meeting?')) return;
    setSaving(true);
    try {
      await deleteMeeting(meeting.id);
      onSaved?.();
    } catch (err) {
      setError('Failed to delete');
      setSaving(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 99990
      }} />
      <div role="dialog" aria-modal="true" style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 'min(540px, 92vw)', maxHeight: '88vh',
        backgroundColor: 'white', borderRadius: '14px',
        boxShadow: '0 20px 50px rgba(0,0,0,0.25)', zIndex: 99995,
        display: 'flex', flexDirection: 'column', overflow: 'hidden'
      }}>
        <div style={{
          padding: '14px 18px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, fontSize: '18px' }}>{isEdit ? 'Edit Meeting' : 'New Meeting'}</h2>
          <button onClick={onClose} aria-label="Close" style={{
            background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px'
          }}>
            <FiX size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '16px 18px', overflowY: 'auto' }}>
          {error && (
            <div style={{
              padding: '8px 10px', backgroundColor: '#fef2f2', color: '#b91c1c',
              borderRadius: '8px', marginBottom: '10px', fontSize: '13px'
            }}>{error}</div>
          )}

          <Field label="Title" required>
            <input
              type="text" value={title} onChange={(e) => setTitle(e.target.value)}
              autoFocus maxLength={200} required style={inputStyle}
              placeholder="Meeting with city engineer"
            />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Field label="Start" required>
              <input
                type="datetime-local" value={startInput}
                onChange={(e) => setStartInput(e.target.value)}
                required style={inputStyle}
              />
            </Field>
            <Field label="End (optional)">
              <input
                type="datetime-local" value={endInput}
                onChange={(e) => setEndInput(e.target.value)}
                style={inputStyle}
              />
            </Field>
          </div>

          <Field label="Reminder">
            <select value={reminder} onChange={(e) => setReminder(parseInt(e.target.value, 10))} style={inputStyle}>
              {REMINDER_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Location (optional)">
            <input
              type="text" value={location} onChange={(e) => setLocation(e.target.value)}
              maxLength={200} style={inputStyle}
              placeholder="Zoom link, address, or place"
            />
          </Field>

          <Field label="Notes (optional)">
            <textarea
              value={description} onChange={(e) => setDescription(e.target.value)}
              rows={3} style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
            />
          </Field>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
            <div>
              {isEdit && (
                <button type="button" onClick={handleDelete} disabled={saving} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  padding: '8px 14px', fontSize: '14px',
                  backgroundColor: 'white', color: '#dc2626',
                  border: '1px solid #fecaca', borderRadius: '8px', cursor: 'pointer'
                }}>
                  <FiTrash2 size={14} /> Delete
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" onClick={onClose} disabled={saving} style={{
                padding: '8px 16px', fontSize: '14px', backgroundColor: 'white',
                border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', color: '#374151'
              }}>Cancel</button>
              <button type="submit" disabled={saving} style={{
                padding: '8px 16px', fontSize: '14px', backgroundColor: '#2563eb',
                border: '1px solid #2563eb', borderRadius: '8px', cursor: saving ? 'not-allowed' : 'pointer',
                color: 'white', fontWeight: 600
              }}>{saving ? 'Saving…' : (isEdit ? 'Save' : 'Create')}</button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: '12px' }}>
      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
        {label}{required && <span style={{ color: '#dc2626' }}> *</span>}
      </label>
      {children}
    </div>
  );
}
